// Structured, GEO-friendly content primitives shared by the CMS editor and the
// public article renderer. These power answer-first capsules, key takeaways,
// first-class FAQs, cited sources, and E-E-A-T author/reviewer signals.
//
// DESIGN: every parser is defensive. Stored values may be a JSON string, an
// already-parsed array/object, null, or malformed legacy data. Nothing here
// should ever throw — existing content with none of these fields must render
// exactly as before. All "empty" inputs return safe empties.

import type { FAQItem } from '@/utils/faqSchema';

export interface SourceRef {
  label: string;
  url: string;
}

export interface AuthorRef {
  name: string;
  title?: string;
  url?: string; // sameAs (e.g. LinkedIn / professional profile)
}

// Structured fields that may live on a dynamic_pages row (all optional). Kept
// loose because the columns may not exist yet (pre-migration) — see the resilient
// read/write in AdminContentEditor and the renderer.
export interface StructuredFields {
  answer_summary?: string | null;
  key_takeaways?: unknown;
  faqs?: unknown;
  sources?: unknown;
  author_name?: string | null;
  author_title?: string | null;
  author_url?: string | null;
  reviewer_name?: string | null;
  reviewer_title?: string | null;
  reviewer_url?: string | null;
  last_reviewed_at?: string | null;
}

/** Safely coerce a jsonb-ish value into an array. Accepts arrays or JSON strings. */
export function parseJsonArray(value: unknown): unknown[] {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

/** Parse key takeaways into a clean string[] (drops blanks). */
export function parseKeyTakeaways(value: unknown): string[] {
  return parseJsonArray(value)
    .map((item) => (typeof item === 'string' ? item : item == null ? '' : String(item)))
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Parse FAQs into clean {question, answer} pairs (tolerates q/a or question/answer keys). */
export function parseFaqs(value: unknown): FAQItem[] {
  return parseJsonArray(value)
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      const obj = item as Record<string, unknown>;
      const question = String(obj.question ?? obj.q ?? '').trim();
      const answer = String(obj.answer ?? obj.a ?? '').trim();
      if (!question || !answer) return null;
      return { question, answer } as FAQItem;
    })
    .filter((x): x is FAQItem => x !== null);
}

/** Parse sources into clean {label, url} pairs (requires a url; labels fall back to the url). */
export function parseSources(value: unknown): SourceRef[] {
  return parseJsonArray(value)
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      const obj = item as Record<string, unknown>;
      const url = String(obj.url ?? '').trim();
      if (!url) return null;
      const label = String(obj.label ?? obj.title ?? '').trim() || url;
      return { label, url } as SourceRef;
    })
    .filter((x): x is SourceRef => x !== null);
}

/** Count words in a (possibly HTML) string. */
export function countWords(html: string | null | undefined): number {
  if (!html) return 0;
  const text = html.replace(/<[^>]*>/g, ' ');
  return text.split(/\s+/).filter(Boolean).length;
}

export interface GeoCheck {
  id: string;
  label: string;
  passed: boolean;
  hint: string;
  weight: number;
}

export interface GeoScoreResult {
  score: number; // 0..100
  checks: GeoCheck[];
  passedCount: number;
  totalCount: number;
}

export interface GeoScoreInput {
  content?: string | null;
  answerSummary?: string | null;
  keyTakeaways?: string[];
  faqs?: FAQItem[];
  sources?: SourceRef[];
  hasAuthor?: boolean;
  hasReviewer?: boolean;
  lastReviewedAt?: string | null;
}

const ANSWER_MIN_WORDS = 40;
const ANSWER_MAX_WORDS = 80;
const FRESH_DAYS = 180;

/**
 * Score a draft against the GEO/AEO rubric. This turns "best practices" into a
 * visible, non-blocking checklist in the editor. It never throws and treats all
 * missing inputs as simply "not yet done".
 */
export function computeGeoScore(input: GeoScoreInput): GeoScoreResult {
  const answerWords = countWords(input.answerSummary);
  const takeaways = input.keyTakeaways ?? [];
  const faqs = input.faqs ?? [];
  const sources = input.sources ?? [];
  const bodyWords = countWords(input.content);

  // Question-shaped headings: at least one H2/H3 that reads like a question.
  const headingText = (input.content ?? '').match(/<h[23][^>]*>([\s\S]*?)<\/h[23]>/gi) || [];
  const hasQuestionHeading = headingText.some((h) => /\?/.test(h) ||
    /^\s*<h[23][^>]*>\s*(how|what|why|when|where|who|which|can|do|does|is|are|should)\b/i.test(h));

  // Freshness: reviewed within FRESH_DAYS.
  let isFresh = false;
  if (input.lastReviewedAt) {
    const t = Date.parse(input.lastReviewedAt);
    if (!Number.isNaN(t)) {
      isFresh = (Date.now() - t) / 86_400_000 <= FRESH_DAYS;
    }
  }

  const checks: GeoCheck[] = [
    {
      id: 'answer',
      label: `Answer capsule (${ANSWER_MIN_WORDS}–${ANSWER_MAX_WORDS} words)`,
      passed: answerWords >= ANSWER_MIN_WORDS && answerWords <= ANSWER_MAX_WORDS,
      hint: answerWords === 0
        ? 'Add a 40–80 word direct answer at the top — AI engines lift these verbatim.'
        : `Currently ${answerWords} words; aim for ${ANSWER_MIN_WORDS}–${ANSWER_MAX_WORDS}.`,
      weight: 20,
    },
    {
      id: 'takeaways',
      label: 'Key takeaways (3+)',
      passed: takeaways.length >= 3,
      hint: 'List 3+ scannable takeaways — highly extractable for AI summaries.',
      weight: 15,
    },
    {
      id: 'faqs',
      label: 'FAQs (3+)',
      passed: faqs.length >= 3,
      hint: 'Add 3+ Q&A pairs; these drive FAQPage schema and answer-engine citations.',
      weight: 20,
    },
    {
      id: 'sources',
      label: 'Cited sources (2+)',
      passed: sources.length >= 2,
      hint: 'Cite 2+ authoritative sources (boards, AANP). Builds YMYL trust & consensus.',
      weight: 15,
    },
    {
      id: 'questionHeadings',
      label: 'Question-shaped heading',
      passed: hasQuestionHeading,
      hint: 'Use at least one H2/H3 phrased as a real user question.',
      weight: 10,
    },
    {
      id: 'depth',
      label: 'Sufficient depth (600+ words)',
      passed: bodyWords >= 600,
      hint: `Body is ${bodyWords} words; aim for 600+ for topical authority.`,
      weight: 10,
    },
    {
      id: 'eeat',
      label: 'Author or medical reviewer',
      passed: Boolean(input.hasAuthor || input.hasReviewer),
      hint: 'Add a named author and/or medical reviewer for E-E-A-T (YMYL health content).',
      weight: 5,
    },
    {
      id: 'fresh',
      label: 'Reviewed in last 6 months',
      passed: isFresh,
      hint: 'Set "Last reviewed" within ~180 days — AI engines favor fresh content.',
      weight: 5,
    },
  ];

  const totalWeight = checks.reduce((sum, c) => sum + c.weight, 0);
  const earned = checks.reduce((sum, c) => sum + (c.passed ? c.weight : 0), 0);
  const score = totalWeight > 0 ? Math.round((earned / totalWeight) * 100) : 0;
  const passedCount = checks.filter((c) => c.passed).length;

  return { score, checks, passedCount, totalCount: checks.length };
}
